import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { PdfViewerModalComponent } from '../../../../shared/components/pdf-viewer-modal/pdf-viewer-modal.component';
import { EmptyPipe } from '../../../../shared/pipes/empty.pipe';
import { LocaleDatetimeFormatPipe } from '../../../../shared/pipes/locale-datetime-format.pipe';
import { NumberToCurrencyPipe } from '../../../../shared/pipes/number-to-currency.pipe';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { ReportKeys } from '../../../reports/core/enums/ReportKeys';
import { ReportsService } from '../../../reports/core/services/ReportsService';
import { ONLINE_ORDER_STATUS_LABELS } from '../../core/constants/online-order-status-labels';
import { OnlineOrderPositionKinds } from '../../core/enums/OnlineOrderPositionKinds';
import { OnlineOrderStatuses } from '../../core/enums/OnlineOrderStatuses';
import { IOnlineOrderDetails } from '../../core/interfaces/IOnlineOrderDetails';
import { IOnlineOrderPosition } from '../../core/interfaces/IOnlineOrderPosition';
import { OnlineOrdersService } from '../../core/services/OnlineOrdersService';
import {
  OnlineOrderCodeInputModalComponent
} from '../online-order-code-input-modal/online-order-code-input-modal.component';

@Component({
  selector: 'app-online-order-details-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    NgTemplateOutlet,
    LoaderComponent,
    EmptyPipe,
    NumberToCurrencyPipe,
    LocaleDatetimeFormatPipe,
    OnlineOrderCodeInputModalComponent,
    PdfViewerModalComponent,
  ],
  templateUrl: './online-order-details-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OnlineOrderDetailsModalComponent extends BaseModalComponent<{ transactionId: number; }> {

  public readonly statusLabels = ONLINE_ORDER_STATUS_LABELS;

  @ViewChild('onlineOrderCodeInputModalComponent') onlineOrderCodeInputModalComponent: OnlineOrderCodeInputModalComponent;
  @ViewChild('pdfViewerModalComponent') pdfViewerModalComponent: PdfViewerModalComponent;

  @Output() fulfilmentCompleted = new EventEmitter<void>();

  public readonly details = signal<IOnlineOrderDetails>(null);
  public readonly isLoading = signal(false);
  public readonly isReportGenerating = signal(false);

  public readonly openPositions = computed(() =>
    (this.details()?.positions ?? []).filter(p => p.status === OnlineOrderStatuses.Open)
  );

  public readonly hasUnsupportedPositions = computed(() =>
    (this.details()?.positions ?? []).some(p => !p.supported)
  );

  public readonly canFulfil = computed(() => {
    const order = this.details();

    return !!order
      && order.status === OnlineOrderStatuses.Open
      && order.positions.length > 0
      && order.positions.every(p => p.supported)
      && this.openPositions().length > 0;
  });

  public readonly canOpenDeliveryNote = computed(() => {
    const order = this.details();

    return !!order && order.status === OnlineOrderStatuses.Issued && !!order.cashTransactionId;
  });

  public readonly positionTextsByPayVoucherId = computed(() => {
    const texts = new Map<number, string>();

    for (const position of this.details()?.positions ?? []) {
      texts.set(position.payVoucherId, this.buildPositionText(position));
    }

    return texts;
  });

  private _positionsToFulfil: IOnlineOrderPosition[] = [];
  private _currentIndex = 0;

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _onlineOrdersService = inject(OnlineOrdersService);
  private readonly _reportsService = inject(ReportsService);

  public override open(val?: { transactionId: number; }): void {
    this.details.set(null);
    super.open(val);
    this.loadDetails();
  }

  public startFulfilmentFor(transactionId: number): void {
    this.details.set(null);
    this.data = { transactionId };
    this.loadDetails(() => {
      if (this.canFulfil()) {
        this.startFulfilment();
      } else {
        super.open(this.data);
      }
    });
  }

  public startFulfilment(): void {
    if (!this.canFulfil()) {
      return;
    }

    this._positionsToFulfil = this.openPositions();
    this._currentIndex = 0;
    this.openCodeInput();
  }

  public fulfilPosition(position: IOnlineOrderPosition, code: string): void {
    this._onlineOrdersService.fulfilPosition(this.details().transactionId, position.payVoucherId, code)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: res => {
          if (OnlineOrdersService.isErrorResponse(res)) {
            this.onFulfilError(res.message, position);
            return;
          }

          this._currentIndex++;

          if (this._currentIndex < this._positionsToFulfil.length) {
            this.openCodeInput();
            return;
          }

          this.onlineOrderCodeInputModalComponent.close();
          this.close();
          this.fulfilmentCompleted.emit();
        },
        error: ({ error }: ApiErrorResponse) => this.onFulfilError(error?.message, position)
      });
  }

  public openDeliveryNote(): void {
    if (!this.canOpenDeliveryNote()) {
      return;
    }

    this.isReportGenerating.set(true);

    this._reportsService.generateReport(ReportKeys.CustomerDelivery, this.details().cashTransactionId)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => this.isReportGenerating.set(false))
      )
      .subscribe({
        next: res => this.pdfViewerModalComponent.open({ base64Content: res.data.base64Content }),
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`online-orders/delivery-note/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private loadDetails(onLoaded?: () => void): void {
    this.isLoading.set(true);

    this._onlineOrdersService.getOnlineOrder(this.data.transactionId)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: res => {
          this.details.set(res.data);
          onLoaded?.();
        },
        error: ({ error }: ApiErrorResponse) => {
          this.close();
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`online-orders/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private openCodeInput(): void {
    this.onlineOrderCodeInputModalComponent.open({
      position: this._positionsToFulfil[this._currentIndex],
      index: this._currentIndex + 1,
      total: this._positionsToFulfil.length
    });
  }

  private buildPositionText(p: IOnlineOrderPosition): string {
    return [
      p.itemName,
      p.itemVariant,
      `${p.amount?.toCurrency()} ${this.languageConfig.currency.short}`
    ]
      .filter(Boolean)
      .join(' · ');
  }

  private onFulfilError(errorKey: string, position: IOnlineOrderPosition): void {
    this.onlineOrderCodeInputModalComponent.setLoading(false);

    if (!errorKey) {
      this.notificationService.pushError(
        'Fehler'.trans('notifications/error.Title'),
        'Ein Fehler ist aufgetreten'.trans('notifications/error.Unknown')
      );
      return;
    }

    this.notificationService.pushError(
      'Fehler'.trans('notifications/error.Title'),
      errorKey.trans(this.errorNamespace(errorKey, position))
    );
  }

  private errorNamespace(errorKey: string, position: IOnlineOrderPosition): string {
    if (errorKey.startsWith('OnlineOrder')) {
      return `online-orders/notifications/${errorKey}.Message`;
    }

    return position.kind === OnlineOrderPositionKinds.Voucher
      ? `cash-voucher-in-cart/notifications/${errorKey}.Message`
      : `card/notifications/${errorKey}.Message`;
  }
}
